import React, { useState, useEffect } from 'react';
import {
  UserIcon,
  CloudArrowUpIcon,
  DocumentTextIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  KeyIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon
} from '@heroicons/react/24/outline';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { baseUrl } from '../../baseurl.js';
axios.defaults.baseURL = baseUrl;

const Settings = () => {
  const [activeTab, setActiveTab] = useState('license');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [days, setDays] = useState(null);
  const [streams, setStreams] = useState({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [expireDate, setExpireDate] = useState('');
  const [userLimit, setUserLimit] = useState(0);
  const [v, setV] = useState(null);
  const [lastSync, setLastSync] = useState('');
  

  const token = localStorage.getItem("vsat_token");

  // Admin profile state
  const [adminInfo, setAdminInfo] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    multicast_ip: ''
  });

  // File upload state
  const [fileUpload, setFileUpload] = useState({
    version: '',
    versionName: '',
    file: null,
    description: '',
    isMandatory: false
  });

  const [uploadHistory, setUploadHistory] = useState([]);

  // Load admin data on component mount
  useEffect(() => {
    loadAdminData();
    loadUploadHistory();
    
  }, []);

  useEffect(() => {
    getChatDay();
    loadStudios();
  },[page]);

  useEffect(()=>{
    getPlan();
  },[]);

  const getPlan = async ()=>{
    const token = localStorage.getItem("vsat_token");
    try{
          const res = await axios.get('admin/get-plan',{
              headers: {
              Authorization: `Bearer ${token}`,
            }
          });

          if(res.data.success)
          {
            //console.log(res.data.plan);
            const planVersion = res.data.plan.version;
            setExpireDate(res.data.plan.expire_date);
            setUserLimit(res.data.plan.user_limit);
            setV(planVersion);
            setLastSync(res.data.plan.updatedAt);
            
          }
    }
    catch(error)
    {
        console.error("Error:", error.response?.data || error.message);
    }
  }

  const loadAdminData = async () => {
    try {
      
      const response = await axios.get('admin/profile', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        console.log(response.data);
        setAdminInfo(prev => ({
          ...prev,
          name: response.data.admin.name || '',
          email: response.data.admin.email || '',
          phone: response.data.admin.phone || '',
          multicast_ip: response.data.admin.multicast_ip || ''
        }));
      }
    } catch (error) {
      //toast.error('Error loading admin data');
    }
  };

  const getChatDay = async () => {
    try {
      const response = await axios.get('admin/get-chat-days', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setDays(response.data.chat.days); // Set the days directly
      }
    } catch (error) {
      setDays(0);
    }
  };

  const loadStudios = async (pageNum = 1) => {
    setLoading(true);
    
    try {
      const today = new Date();
      
      const token = localStorage.getItem("vsat_token");
      const response = await axios.get('admin/latest-studios', {
        params: { page: pageNum, limit: 10 },
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });

      if (response.data.success) {
        
        setStreams(response.data.studios);
        
        setTotalPages(response.data.totalPages);
        setPage(response.data.currentPage);
        
      } else {
        toast.error("Failed to fetch studios");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error loading studios");
    } finally {
      setLoading(false);
    }
  };

  const loadUploadHistory = async () => {
    try {
      const token = localStorage.getItem("vsat_token");
      const response = await axios.get('admin/upload-history', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        
        setUploadHistory(response.data.uploads || []);
      }
    } catch (error) {
      //console.error('Error loading upload history');
    }
  };

  const handleAdminInfoChange = (field, value) => {
    setAdminInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUploadChange = (field, value) => {
    setFileUpload(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check if file is an executable
      const isExe = file.name.toLowerCase().endsWith('.exe');
      if (!isExe) {
        toast.error('Please select a valid .exe file');
        return;
      }

      // Check file size (max 100MB)
      if (file.size > 100 * 1024 * 1024) {
        toast.error('File size must be less than 100MB');
        return;
      }

      setFileUpload(prev => ({
        ...prev,
        file: file
      }));
    }
  };

  const updateAdminProfile = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem("vsat_token");
      const updateData = {
        name: adminInfo.name,
        email: adminInfo.email,
        phone: adminInfo.phone,
        multicast_ip: adminInfo.multicast_ip
      };

      // Add password fields only if they are provided
      if (adminInfo.password != '') {
        if (adminInfo.password !== adminInfo.confirmPassword) {
          toast.error('New passwords do not match');
          setLoading(false);
          return;
        }
    
        updateData.password = adminInfo.password;
      }

      const response = await axios.post('admin/update-profile', updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('Profile updated successfully');
        // Clear password fields
        setAdminInfo(prev => ({
          ...prev,
          password: '',
          confirmPassword: ''
        }));
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating profile');
    } finally {
      setLoading(false);
    }
  };

  const uploadExecutable = async (e) => {
    e.preventDefault();
    
    if (!fileUpload.file || !fileUpload.version || !fileUpload.versionName) {
      toast.error('Please fill all required fields');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', fileUpload.file);
      formData.append('version', fileUpload.version);
      formData.append('versionName', fileUpload.versionName);
      formData.append('description', fileUpload.description);
      formData.append('isMandatory', fileUpload.isMandatory);

      const token = localStorage.getItem("vsat_token");
      const response = await axios.post('admin/upload-exe', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        toast.success('Executable uploaded successfully');
        setFileUpload({
          version: '',
          versionName: '',
          file: null,
          description: '',
          isMandatory: false
        });
        document.getElementById('file-input').value = '';
        loadUploadHistory();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error uploading file');
    } finally {
      setUploading(false);
    }
  };

  const deleteVersion = async (versionId) => {
    if (!window.confirm('Are you sure you want to delete this version?')) {
      return;
    }

    try {
      const token = localStorage.getItem("vsat_token");
      const response = await axios.delete(`admin/delete-exe/${versionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('Version deleted successfully');
        loadUploadHistory();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting version');
    }
  };

  const handleDays = async (e)=>{
      e.preventDefault();
       try {
            const response = await axios.post('admin/store-chat-backup', {
            days
        }, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data.success) {
          toast.success(response.data.message);
          setDays(response.data.chat.days);
          
        } else {
          toast.error("Failed to save chat");
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Error saving studio");
      }
      console.log(days);
  }

  const handleDownload = async (id, originalName) => {
    try {
      const token = localStorage.getItem("vsat_token");
      const res = await axios.get(`admin/download-exe/${id}`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` }
      });

      const blob = new Blob([res.data], { type: 'application/x-msdownload' });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = originalName; // saved file name
      document.body.appendChild(a);
      a.click();

      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      
      toast.error("Error downloading file");
    }
  };

  const handleChatDelete = async (studio_id)=>{
      try 
      {
        const token = localStorage.getItem("vsat_token");
        const res = await axios.delete(`admin/delete-stream-chat/${studio_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if(res.data.success)
        {
          toast.success(res.data.message);
        }
        toast.error(res.data.message);
    } catch (err) {
      toast.error(err.message);
    }
  }

  const StatCard = ({ title, value, icon: Icon, trend, description }) => (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between">
          <div className='flex items-center justify-center'>
            <p className="text-2xl font-medium text-gray-600 mr-8">{title} :</p>
            <p className="text-2xl font-medium text-white mt-2 border px-2 py-1 rounded bg-blue-600">{value}</p>
            
          </div>
          <div className={`p-3 rounded-lg bg-slate-500`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
        {trend && (
          <div className={`flex items-center mt-3 text-sm ${trend.value > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend.value > 0 ? (
              <ArrowTrendingUpIcon className="w-4 h-4 mr-1" />
            ) : (
              <ArrowTrendingDownIcon className="w-4 h-4 mr-1" />
            )}
            <span>{Math.abs(trend.value)}% {trend.label}</span>
          </div>
        )}
      </div>
    );

  const tabs = [
    { id: 'license', name: 'License', icon: KeyIcon },
    { id: 'profile', name: 'Admin Profile', icon: UserIcon },
    { id: 'upload', name: 'Executable Upload', icon: CloudArrowUpIcon },
    { id: 'history', name: 'Upload History', icon: DocumentTextIcon },
    { id: 'chat', name: 'Manage Chat', icon: ChatBubbleOvalLeftEllipsisIcon },
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600 mt-2">Manage your admin profile and application updates</p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="w-5 h-5 mr-2" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Admin Profile Tab */}
            {activeTab === 'license' && (
              <div className="max-w-2xl">
                <StatCard
          title="ClassRoom Limits"
          value={userLimit}
          icon={ClockIcon}
          description="Total ClassRoom Limit"
        />
        <StatCard
          title="Expiry At"
          value={expireDate ? new Date(expireDate).toLocaleDateString() : new Date().toLocaleDateString()}
          icon={ClockIcon}
          description="Admin Software Expiry at"
        />
              </div>
            )}
            {/* Admin Profile Tab */}
            {activeTab === 'profile' && (
              <div className="max-w-2xl">
                <div className="flex items-center mb-6">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <UserIcon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <h2 className="text-xl font-semibold text-gray-900">Admin Profile</h2>
                    <p className="text-gray-600">Update your personal information and password</p>
                  </div>
                </div>

                <form onSubmit={updateAdminProfile} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={adminInfo.name}
                        onChange={(e) => handleAdminInfoChange('name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        value={adminInfo.email}
                        onChange={(e) => handleAdminInfoChange('email', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={adminInfo.phone}
                        onChange={(e) => handleAdminInfoChange('phone', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Multicast IP
                      </label>
                      <input
                        type="text"
                        value={adminInfo.multicast_ip}
                        onChange={(e) => handleAdminInfoChange('multicast_ip', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  {/* Password Change Section */}
                  <div className="border-t border-gray-200 pt-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Change Password</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                     
                      <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            New Password
                          </label>
                          <input
                            type="password"
                            name="password"
                            value={adminInfo.password}
                            onChange={(e) => handleAdminInfoChange('password', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Minimum 6 characters"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Confirm New Password
                          </label>
                          <input
                            type="password"
                            name="confirmPassword"
                            value={adminInfo.confirmPassword}
                            onChange={(e) => handleAdminInfoChange('confirmPassword', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end pt-4">
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                      {loading ? 'Updating...' : 'Update Profile'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Executable Upload Tab */}
            {activeTab === 'upload' && (
              <div className="max-w-2xl">
                <div className="flex items-center mb-6">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CloudArrowUpIcon className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <h2 className="text-xl font-semibold text-gray-900">Upload Executable</h2>
                    <p className="text-gray-600">Upload new application versions for users</p>
                  </div>
                </div>

                <form onSubmit={uploadExecutable} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Version Number *
                      </label>
                      <input
                        type="text"
                        value={fileUpload.version}
                        onChange={(e) => handleFileUploadChange('version', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., 1.2.3"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Version Name *
                      </label>
                      <input
                        type="text"
                        value={fileUpload.versionName}
                        onChange={(e) => handleFileUploadChange('versionName', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., Stable Release"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={fileUpload.description}
                      onChange={(e) => handleFileUploadChange('description', e.target.value)}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Describe what's new in this version..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Executable File *
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      <input
                        id="file-input"
                        type="file"
                        accept=".exe"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      <CloudArrowUpIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-sm text-gray-600 mb-2">
                        {fileUpload.file 
                          ? `Selected: ${fileUpload.file.name}` 
                          : 'Click to select .exe file or drag and drop'
                        }
                      </p>
                      <p className="text-xs text-gray-500 mb-4">
                        Maximum file size: 500MB
                      </p>
                      <button
                        type="button"
                        onClick={() => document.getElementById('file-input').click()}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        Choose File
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="mandatory"
                      checked={fileUpload.isMandatory}
                      onChange={(e) => handleFileUploadChange('isMandatory', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="mandatory" className="ml-2 text-sm text-gray-700">
                      Mandatory update (Users must install this version)
                    </label>
                  </div>

                  <div className="flex justify-end pt-4">
                    <button
                      type="submit"
                      disabled={uploading || !fileUpload.file}
                      className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                    >
                      {uploading ? 'Uploading...' : 'Upload Version'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Upload History Tab */}
            {activeTab === 'history' && (
              <div>
                <div className="flex items-center mb-6">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <DocumentTextIcon className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <h2 className="text-xl font-semibold text-gray-900">Upload History</h2>
                    <p className="text-gray-600">Previously uploaded application versions</p>
                  </div>
                </div>

                {uploadHistory.length === 0 ? (
                  <div className="text-center py-12">
                    <DocumentTextIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No upload history found</p>
                  </div>
                ) : (
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Version
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Name
                          </th>
                          
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Upload Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {uploadHistory.map((item) => (
                          <tr key={item._id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                              <div className="text-sm font-medium text-gray-900 ">
                                {item.version}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900 capitalize">{item.versionName}</div>
                              {item.description && (
                                <div className="text-xs text-gray-500 mt-1">
                                  {item.description}
                                </div>
                              )}
                            </td>
                            
                            <td className="px-6 py-4 text-sm text-gray-500">
                              {new Date(item.createdAt).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => handleDownload(item._id, item.filePath)}
                                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                                >
                                  <ArrowDownTrayIcon className="w-4 h-4 text-green-600 mx-auto mb-4"/>
                                </button>
                                <button
                                  onClick={() => deleteVersion(item._id)}
                                  className="text-red-600 hover:text-red-700 text-sm font-medium"
                                >
                                  <TrashIcon className="w-4 h-4 text-red-500 mx-auto mb-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Upload History Tab */}
            {activeTab === 'chat' && (
              <div>
                <div className="flex items-center mb-6">
                  
                  <div className="ml-4">
                    <h2 className="text-xl font-semibold text-gray-900">Auto Delete Chat</h2>
                    <h4 className="text-sm font-semibold text-gray-400">
                      After Stream Chat Delete In Days
                    </h4>
                    
                  </div>
                </div>

                <div className='grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-2'>
                    <div className="text-center py-12">
                      <form  className="max-w-md mx-auto p-6 bg-white rounded-lg shadow-md" onSubmit={handleDays}>
                                
                        {/* Name Field */}
                        <div className="mb-4">
                          <label htmlFor="name" className="block text-left text-sm font-medium text-gray-700 mb-1">
                            Days
                          </label>
                          <input
                            type="number"
                            name="days"
                            value={days} // Use days state directly
                            onChange={(e) => setDays(parseInt(e.target.value) || 0)} 
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Enter Days"
                          />
                        </div>


                        {/* Submit Button */}
                        <button
                          type="submit"
                          className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200"
                        >
                          SUBMIT
                        </button>
                      </form>
                    </div>
                    <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="text-xl font-semibold text-gray-800">Recent Streams</h4>
                        <span className="text-sm text-gray-500">Last {days || 7} Days</span>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="min-w-full border border-gray-200">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Name</th>
                              <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Date</th>
                              <th className="px-4 py-2 text-center text-sm font-medium text-gray-700">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {streams.length > 0 ? (
                              streams.map((stream, index) => (
                                <tr key={index} className="border-t hover:bg-gray-50 transition">
                                  <td className="px-4 py-2 text-sm text-gray-800">{stream.studio_name}</td>
                                  <td className="px-4 py-2 text-sm text-gray-600">
                                    {new Date(stream.date).toLocaleDateString()}
                                  </td>
                                  <td className="px-4 py-2 text-center">
                                    <div className="flex flex-col items-center gap-1">
                                      <button
                                        onClick={() => handleChatDelete(stream._id)}
                                        className="bg-red-500 text-white text-sm px-3 py-1 rounded hover:bg-red-600 transition"
                                      >
                                        Delete
                                      </button>
                                      <span className="text-xs text-gray-500">
                                        Expires in:{" "}
                                        {Math.max(
                                          0,
                                          Math.ceil(
                                            (new Date(stream.date).getTime() + days * 24 * 60 * 60 * 1000 - Date.now()) /
                                              (1000 * 60 * 60 * 24)
                                          )
                                        )}{" "}
                                        days
                                      </span>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td
                                  colSpan="3"
                                  className="text-center py-4 text-gray-500 text-sm"
                                >
                                  No streams found
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>

                          {/* Pagination */}
                          <div className="mt-6 flex items-center justify-between">
                            <div className="text-sm text-gray-700">
                              Showing page {page} of {totalPages}
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <button
                                disabled={page === 1 || loading}
                                onClick={() => setPage(page - 1)}
                                className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                  page === 1 || loading
                                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                    : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                                }`}
                              >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                                Previous
                              </button>

                              {/* Page Numbers */}
                              <div className="flex gap-1">
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                                  <button
                                    key={pageNum}
                                    onClick={() => setPage(pageNum)}
                                    disabled={loading}
                                    className={`min-w-[40px] px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                      page === pageNum
                                        ? "bg-blue-600 text-white"
                                        : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                                    } ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
                                  >
                                    {pageNum}
                                  </button>
                                ))}
                              </div>

                              <button
                                disabled={page === totalPages || loading || totalPages === 0}
                                onClick={() => setPage(page + 1)}
                                className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                  page === totalPages || loading || totalPages === 0
                                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                    : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                                }`}
                              >
                                Next
                                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </button>
                            </div>
                          </div>
                      </div>
                    </div>
                </div>
        
                  
                  
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;